import { Link } from 'react-router-dom';
import { PolicyMeta } from '../components/PolicyMeta';
import { perDay, useSite } from '../site/SiteProvider';

/**
 * 利用規約（`/terms`）。doc/AUTH.md §13 で決めた中身を本文にしたもの。
 *
 * 書き方はヘルプ・診断の文面と同じで、断定せず、できないことはできないと書く。
 * この本文を直したら、`services/api/src/lib/terms.ts` の `TERMS_VERSION` を上げる（次のログインで同意を取り直す）。
 * 節の番号は出した順に振る（`no()`）。
 */
export function TermsPage() {
  const { limits } = useSite();
  let count = 0;
  const no = () => `${++count}. `;
  return (
    <main className="page policy">
      <h1 className="page__title">利用規約</h1>
      <PolicyMeta kind="terms" />
      <p className="page__lead">
        GenDan（Generative AI Shindan）をお使いいただくときの約束です。あわせて <Link to="/privacy">プライバシーポリシー</Link> もご覧ください。
      </p>

      <section className="section">
        <h2 className="section__title">{no()}このサービスについて</h2>
        <p>
          GenDan は、いくつかの問いに答えて自分に近いキャラクターを知る診断と、テーマを書いて診断そのものを作る機能を提供するウェブサイトです。運営しているのは <strong>x-type</strong> です。
        </p>
        <p>
          予告なく内容を変えたり、止めたりすることがあります。保存したデータが失われることもあります。大切な記録の保管場所としてはお使いにならないでください。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">{no()}診断の結果について</h2>
        <p>
          この診断は <strong>遊びであって、専門家が行う心理検査ではありません</strong>。結果は「答えた内容から見える傾向」であって、あなたを言い当てるものではありません。同じ人でも、体調や状況によって答えは変わります。
        </p>
        <p>
          信頼性や妥当性は、まだ実測できるだけのデータが集まっていません。
          <strong>採用・選抜・人事評価の判断材料としてはお使いにならないでください。</strong>
          結果には軸ごとの「確からしさ」を出しています。短い版は測る側面が少なく、そのぶん幅があります。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">{no()}作った診断の扱い</h2>
        <ul className="policy__list">
          <li>作った診断は <strong>URL を知る人なら誰でも受けられる公開ページ</strong> になります。受けられた回数が多いと、トップの人気の一覧（上位 12 件）にも並びます。</li>
          <li>ログインせずに作った診断には所有者がいないため、<strong>誰でも消せます</strong>。残しておきたいときはログインしてから作ってください。</li>
          <li>診断を消すと、一覧と入口からは消えます。<strong>すでに出ている結果ページは残ります</strong>（受けた人の結果まで消さないためです）。</li>
          <li>問題のある診断は、運営者が消すことがあります。消した記録は残ります。問題のある診断を見つけたら、入口や結果のページの下にある「この診断を通報する」からお知らせください（ログインは要りません）。</li>
          <li>テーマの文章は、キャラクターを考えるために Gemini（Google Cloud の Vertex AI）へ送られます。個人を特定できることは書かないでください。</li>
          <li>相性の詳細版の「AI の読み」は、Gemini が書いた文章です。誤りや思い込みを含むことがあり、関係の行方や相手の人柄を判定するものではありません。</li>
        </ul>
      </section>

      <section className="section">
        <h2 className="section__title">{no()}してはいけないこと</h2>
        <ul className="policy__list">
          <li>他人を傷つけるテーマ、差別的なテーマ、わいせつなテーマで診断を作ること。</li>
          <li>実在の個人を特定できる文章を書くこと（自分以外の氏名・連絡先・勤務先など）。</li>
          <li>他人になりすますこと。</li>
          <li>自動化した大量の作成・受検など、サービスの運営を妨げること。</li>
          <li>法令に反すること、商標や著作権を侵すこと。</li>
        </ul>
        <p>
          これらが見つかったときは、診断の削除やアカウントの停止をすることがあります。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">{no()}アカウント</h2>
        <p>
          診断を受けるのも作るのも、ログインは要りません。ログインが要るのは、作った診断にキャラクターの絵を付けるとき、自分のページを使うとき、相性の詳細版（相手の結果と比べる・AI の読み・その PDF）を使うとき、お問い合わせや相性ドラマの動画のリクエストを送るときです。
        </p>
        <p>
          費用の歯止めとして、絵の生成とログインせずに作れる診断の数に、1 人あたりの上限を設けています（いまは絵が{perDay(limits.userImageDailyLimit, '回', '止めていて 0 回')}、ログインせずに作れる診断が{perDay(limits.anonThemeDailyLimit, '回', '0 回でログインが必要')}）。この回数は運営の都合で変えることがあります。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">{no()}免責</h2>
        <p>
          このサービスは現状のまま提供されます。診断の結果や、サービスを使ったことで生じた損害について、運営者は責任を負いかねます。
        </p>
        <p>
          このサイトには広告（アフィリエイト広告を含み、「広告」「PR」と書いた枠）を載せることがあります。<strong>広告の商品やサービスの内容は、それぞれの広告主の責任</strong>です。申し込みや購入は、広告主のサイトの条件をご確認のうえ行ってください。広告の配信事業者との情報のやりとりは <Link to="/privacy">プライバシーポリシー</Link> の 5. に書いています。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">{no()}この規約の変更</h2>
        <p>
          内容を変えるときは、この本文と「版」を更新します。変更したあとの最初のログインで、もう一度同意をお願いします（黙って差し替えることはしません）。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">{no()}お問い合わせ</h2>
        <p>
          このサービスについてのお問い合わせ、診断結果の削除のご依頼は、
          <Link to="/contact">お問い合わせのフォーム</Link> からお送りください。運営は x-type です。
        </p>
        <p>
          <strong>フォームのご利用にはログインが必要です。</strong>どなたからのご依頼かを確かめるためと、自分の診断やアカウントについてのご依頼を、ご本人のものとして扱うためです。結果について訊くときは、結果ページの URL を添えていただけると早く調べられます。
        </p>
        <div className="page__actions">
          <Link className="button button--primary" to="/contact">お問い合わせ</Link>
          <Link className="button button--ghost" to="/privacy">プライバシーポリシー</Link>
          <Link className="button button--ghost" to="/help">ヘルプ</Link>
        </div>
      </section>
    </main>
  );
}
